import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useToastStore } from '@stores/toastStore';
import { collectDroppedFiles } from './gltf-utils';

interface ImportDropZoneProps {
  onFile: (file: File) => void;
  onUrl: (url: string) => void;
  /** 多文件导入（.gltf + 外部 .bin/纹理文件夹） */
  onFiles?: (files: File[]) => void;
}

export function ImportDropZone({ onFile, onUrl, onFiles }: ImportDropZoneProps) {
  const { t } = useTranslation();
  const [dragging, setDragging] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const addToast = useToastStore((s) => s.addToast);

  // 部署在 iframe 中时，浏览器默认 dragover/drop 行为（打开文件）会拦截 drop 事件，
  // 导致拖拽到选择框无效。在 window 级别 preventDefault 确保 drop 能到达组件。
  useEffect(() => {
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('dragover', preventDefaults);
    window.addEventListener('drop', preventDefaults);
    return () => {
      window.removeEventListener('dragover', preventDefaults);
      window.removeEventListener('drop', preventDefaults);
    };
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      void (async () => {
        const files = await collectDroppedFiles(e.dataTransfer);
        if (files.length === 0) return;

        const hasGltf = files.some((f) => f.name.endsWith('.gltf'));
        if (hasGltf && onFiles) {
          onFiles(files);
          return;
        }

        const glbFile = files.find((f) => f.name.endsWith('.glb'));
        if (glbFile) {
          onFile(glbFile);
        } else {
          addToast(t('assetLibrary.unsupportedFormat'), 'warning');
        }
      })();
    },
    [onFile, onFiles, addToast, t],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && (file.name.endsWith('.glb') || file.name.endsWith('.gltf'))) {
        // 独立 .gltf（embedded data URIs）可直接加载；缺少外部资源时由 importFile 报错
        onFile(file);
      }
      e.target.value = '';
    },
    [onFile],
  );

  const handleFolderSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      if (files.length > 0 && onFiles) {
        onFiles(files);
      }
      e.target.value = '';
    },
    [onFiles],
  );

  const handleUrlSubmit = useCallback(() => {
    const trimmed = urlInput.trim();
    if (trimmed) {
      onUrl(trimmed);
      setUrlInput('');
    }
  }, [urlInput, onUrl]);

  return (
    <div className="flex flex-col gap-2">
      {/* 拖拽区域 */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center gap-1 rounded-xl border border-dashed py-6 transition-colors ${
          dragging
            ? 'border-white/40 bg-white/10'
            : 'border-white/15 bg-white/5 hover:border-white/25 hover:bg-white/[0.07]'
        }`}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-5 text-white/40"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <span className="text-xs text-white/60">{t('assetLibrary.dropHint')}</span>
        <input
          ref={fileInputRef}
          type="file"
          accept=".glb,.gltf"
          className="hidden"
          onChange={handleFileSelect}
        />
        {/* 文件夹选择器：用于 .gltf + 外部资源文件夹导入 */}
        <input
          ref={folderInputRef}
          type="file"
          // @ts-expect-error webkitdirectory 是非标准属性
          webkitdirectory=""
          directory=""
          multiple
          className="hidden"
          onChange={handleFolderSelect}
        />
      </div>

      {/* 文件夹导入按钮 */}
      {onFiles && (
        <button
          type="button"
          onClick={() => folderInputRef.current?.click()}
          className="self-start text-[10px] text-white/40 transition-colors hover:text-white/60"
        >
          {t('assetLibrary.folderSelect')}
        </button>
      )}

      {/* URL 导入 */}
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleUrlSubmit();
          }}
          placeholder={t('assetLibrary.urlPlaceholder')}
          className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs text-white/75 placeholder:text-white/30 focus:border-white/20 focus:outline-none"
        />
        <Button
          size="sm"
          variant="secondary"
          onPress={handleUrlSubmit}
          className="h-7 min-w-12 rounded-lg bg-white/10 px-2 text-[11px] text-white/75 hover:bg-white/15"
        >
          {t('assetLibrary.import')}
        </Button>
      </div>
    </div>
  );
}
